// PortalInterviews: the candidate's interviews with Nathan, on the CRM layout.
// Each interview is a card with what it is, how long, until when, and Start.
// The side rail covers how to get ready, and offers Lexa to practise first.
// P2-PORTAL (22 Oct 2026)
import { useState, useEffect } from 'react';
import API_BASE from '../../../config';
import { Ico, ICONS, ukDate } from './portalKit';

const LABEL = { open: 'Waiting for you', completed: 'Finished', expired: 'Closed' };

export default function PortalInterviews({ getToken, go }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch(API_BASE + '/api/portal/interviews', { headers: { Authorization: 'Bearer ' + token } });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.message || j.error || 'Could not load your interviews.');
        if (live) setRows(j.interviews || []);
      } catch (e) { if (live) setError(e.message); }
    })();
    return () => { live = false; };
  }, [getToken]);

  const open = rows ? rows.filter((r) => r.status === 'open') : [];

  return (
    <div className="gtr2-body">
      <div className="gtr2-main">
        <div className="gtr-card gtr2-tabcard">
          <div className="gtp2-cardhead">
            <div><b>Your interviews</b><span>Short video interviews with Nathan, our AI interviewer. Take them whenever suits you. A person at Generative Talent Co reviews every one.</span></div>
            {rows && <span className="gtr2-count">{rows.length}</span>}
          </div>
          <div className="gtr2-pad gtp2-stack">
            {error && <div className="gtr2-empty">{error}</div>}
            {!error && rows === null && <div className="gtr2-empty">Loading your interviews</div>}
            {!error && rows && rows.length === 0 && (
              <div className="gtr2-soon">
                <span className="gtr2-soon-ico"><Ico d={ICONS.chat} /></span>
                <b>No interviews yet</b>
                <p>When a role you have applied for includes an interview, it appears here and we email you the link.</p>
              </div>
            )}
            {!error && rows && rows.map((r) => (
              <article key={r.id} className={'gtp2-iv gtp2-iv--' + r.status}>
                <span className="gtptiv-orb" aria-hidden="true" />
                <div className="gtp2-iv-main">
                  <div className="gtp2-iv-top">
                    <b>{r.title}</b>
                    <span className={'gtp2-tag gtp2-tag--iv-' + r.status}>{LABEL[r.status] || r.status}</span>
                  </div>
                  {r.status === 'open' && (
                    <div className="gtp2-facts">
                      <span><Ico d={ICONS.clock} />About {r.minutes} minutes</span>
                      {r.expires_at && <span><Ico d={ICONS.lock} />Open until {ukDate(r.expires_at, true)}</span>}
                      {r.started && <span className="gtp2-fact--teal"><Ico d={ICONS.route} />You have started. Pick up where you left off</span>}
                    </div>
                  )}
                  {r.status === 'completed' && <p className="gtp2-iv-note">Finished {ukDate(r.completed_at, true)}. Someone at Generative Talent Co is reviewing it and will be in touch by email.</p>}
                  {r.status === 'expired' && <p className="gtp2-iv-note">This interview has closed. Reply to our email if you would still like to take part.</p>}
                </div>
                {r.status === 'open' && r.token && (
                  <a className="gtr2-btn gtr2-btn--primary gtp2-iv-go" href={'/interview/' + r.token}>{r.started ? 'Continue' : 'Start interview'}</a>
                )}
              </article>
            ))}
          </div>
        </div>
      </div>

      <aside className="gtr2-side">
        <div className="gtr-card">
          <div className="gtr-cardh">Before you start</div>
          <ul className="gtp2-checks">
            <li><Ico d={ICONS.cam} /><span><b>Camera on</b>It is a recorded video call, so the person who reviews it sees you as an interviewer would.</span></li>
            <li><Ico d={ICONS.mic} /><span><b>A quiet spot</b>Live captions show what Nathan hears, so background noise matters.</span></li>
            <li><Ico d={ICONS.clock} /><span><b>About 25 minutes</b>If the call drops, open the link again and carry on.</span></li>
            <li><Ico d={ICONS.user} /><span><b>A person decides</b>Nathan does not reject anyone. Someone at Generative Talent Co reviews every interview.</span></li>
          </ul>
        </div>
        <div className="gtr-card gtp2-sidelexa">
          <div className="gtp2-sidelexa-h"><span className="gtlx-orb" aria-hidden="true" /><b>Practise first</b></div>
          <p>Run a mock interview with Lexa, our AI coach, before the real one. She coaches you after each answer and writes up how to sharpen it.</p>
          <button type="button" className="gtr2-btn gtp2-btn--lexa" onClick={() => go('Lexa', { kind: 'interview', role: (open[0] && open[0].role_ref) || '' })}>
            {open.length ? 'Practise for ' + open[0].title : 'Open Lexa'}
          </button>
          <p className="gtp2-prep-fine">Separate from Nathan. Practice is private and never affects your interview.</p>
        </div>
      </aside>
    </div>
  );
}
